import React from 'react';
import { motion } from 'framer-motion';
import { Section, Container, Badge } from '../components/UIComponents';
import { Button } from '../components/Button';
import { ChevronRight, Award, Users, CheckCircle, Heart, Shield, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const HeroSection = () => {
  return (
    <section className="bg-gradient-to-br from-slate-50 via-teal-50 to-blue-50 pt-8 md:pt-16 pb-16 md:pb-28">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto"
        >
          <Badge variant="primary">About Our Company</Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight mt-6">
            Premium <span className="text-teal-600">Surgical Equipment</span> for Healthcare Excellence
          </h1>
          <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
            We are committed to providing the highest quality surgical equipment and medical supports to healthcare professionals across India.
          </p>
        </motion.div>
      </Container>
    </section>
  );
};

const OurStorySection = () => {
  return (
    <Section title="Our Story" subtitle="Built on trust, quality, and healthcare excellence" className="bg-white">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left - Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-teal-200 to-blue-200 rounded-2xl blur-3xl opacity-30"></div>
            <img 
              src="/healthpic.jpeg" 
              alt="Our Story" 
              className="relative w-full max-w-md rounded-2xl shadow-2xl object-contain"
            />
          </motion.div>

          {/* Right - Content */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            <p className="text-lg text-slate-700 leading-relaxed">
              HCS Enterprises began with a simple mission: to provide hospitals, clinics, and healthcare professionals with premium surgical equipment and medical supports at competitive prices.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed">
              We understand that quality medical equipment is critical to patient care and healthcare professional's success. Every product in our catalog is carefully selected and tested to ensure it meets the highest standards of safety and performance.
            </p>

            <p className="text-lg text-slate-700 leading-relaxed">
              Our dedicated team works tirelessly to source, quality-check, and deliver products that make a real difference in healthcare delivery across India.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <span className="font-semibold text-slate-700">ISO Certified</span>
              </div>
              <div className="flex items-center gap-3">
                <Award className="text-amber-600" size={24} />
                <span className="font-semibold text-slate-700">Industry Leader</span>
              </div>
              <div className="flex items-center gap-3">
                <Heart className="text-red-600" size={24} />
                <span className="font-semibold text-slate-700">Patient Focused</span>
              </div>
            </div>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

const OurMissionSection = () => {
  return (
    <Section className="bg-gradient-to-r from-teal-600 to-blue-600 text-white" title="Our Mission">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <Heart className="mx-auto" size={48} />
            <h3 className="text-2xl font-bold">Patient Care</h3>
            <p className="text-teal-100">
              Every product we sell directly impacts patient care and recovery. We take that responsibility seriously.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="space-y-4"
          >
            <Shield className="mx-auto" size={48} />
            <h3 className="text-2xl font-bold">Quality Assurance</h3>
            <p className="text-teal-100">
              All products are ISO certified and undergo rigorous testing to ensure maximum safety and performance.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-4"
          >
            <Zap className="mx-auto" size={48} />
            <h3 className="text-2xl font-bold">Excellence</h3>
            <p className="text-teal-100">
              We continuously innovate and improve our products to deliver the best healthcare solutions.
            </p>
          </motion.div>
        </div>
      </Container>
    </Section>
  );
};

const WhyChooseUsSection = () => {
  const reasons = [
    {
      icon: '✓',
      title: 'Surgical Business Expertise',
      description: 'Specialized in surgical equipment with deep knowledge of healthcare industry needs',
    },
    {
      icon: '🚚',
      title: 'Fast & Reliable Delivery',
      description: 'Deliver medical equipment within 24-48 hours across India with tracking',
    },
    {
      icon: '💰',
      title: 'Competitive Pricing',
      description: 'Direct sourcing allows us to offer premium quality at the best prices',
    },
    {
      icon: '🤝',
      title: 'Professional Support',
      description: '24/7 customer support team with healthcare industry experience',
    },
    {
      icon: '✔️',
      title: 'Quality Guaranteed',
      description: 'All products ISO certified with 30-day money-back guarantee',
    },
    {
      icon: '📞',
      title: 'Always Available',
      description: 'Dedicated support team ready to help you choose the right products',
    },
  ];

  return (
    <Section title="Why Choose HCS Enterprises" subtitle="Leading provider of surgical equipment in India" className="bg-slate-50">
      <Container>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {reasons.map((reason, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="bg-white p-8 rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="text-4xl mb-4">{reason.icon}</div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{reason.title}</h3>
              <p className="text-slate-600">{reason.description}</p>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  );
};

const OurTeamSection = () => {
  return (
    <Section title="Trusted by Healthcare Professionals" subtitle="Serving 500+ hospitals, clinics, and healthcare centers">
      <Container>
        <div className="text-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-xl text-slate-700 max-w-2xl mx-auto leading-relaxed"
          >
            Our commitment to quality and customer satisfaction has made us the preferred choice for surgical equipment among healthcare professionals across India. We pride ourselves on building long-term relationships based on trust, reliability, and excellence.
          </motion.p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-blue-600 mb-2">5 Years+</div>
              <p className="text-slate-600 font-semibold">Industry Experience</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">10,000+</div>
              <p className="text-slate-600 font-semibold">Products Sold</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-5xl font-bold text-teal-600 mb-2">98%</div>
              <p className="text-slate-600 font-semibold">Customer Satisfaction</p>
            </motion.div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

const CTASection = () => {
  const navigate = useNavigate();

  return (
    <Section className="bg-gradient-to-r from-teal-600 to-blue-600 text-white">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center py-8"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Partner With Us?
          </h2>
          <p className="text-lg text-teal-100 mb-8 max-w-2xl mx-auto">
            Experience premium surgical equipment trusted by healthcare professionals. Join thousands of satisfied customers.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/products')}
            className="px-8 py-4 text-lg font-semibold rounded-lg bg-white text-teal-600 hover:bg-slate-100 transition-all duration-200 shadow-lg flex items-center gap-2 mx-auto"
          >
            Shop Now
            <ChevronRight size={20} />
          </motion.button>
        </motion.div>
      </Container>
    </Section>
  );
};

export default function About() {
  return (
    <div className="w-full bg-white">
      <HeroSection />
      <OurStorySection />
      <OurMissionSection />
      <WhyChooseUsSection />
      <OurTeamSection />
      <CTASection />
    </div>
  );
}
